<!--
  Program shell — the profile panel takes the page over when opened
  from the header, otherwise the shop renders.
-->
@if (isProfileOpen) {
  <section class="profile-inline-panel tw-px-[28px] tw-pt-[16px]" data-cy="program-profile">
    <!-- Favorites stay visible above the profile takeover -->
    <ng-container *ngTemplateOutlet="favoritesBar"></ng-container>
    <feature-mat-profile/>
  </section>
} @else {
  <section class="program tw-px-[28px] tw-pt-[16px]" data-cy="program-screen">

    <!-- Favorites row — signed-in only, same chips as wager / results / replays -->
    <ng-container *ngTemplateOutlet="favoritesBar"></ng-container>

    <!-- Program Shop pill -->
    <div class="tw-flex">
      <div
        class="
          tw-relative
          tw-bottom-[-18px]
          tw-z-[-1]
          tw-capitalize
          tw-bg-[#769ED1]
          tw-text-center
          tw-text-[18px]
          tw-text-[#FFFFFF]
          tw-rounded-[4px]
          tw-pt-[5px]
          tw-pb-[12px]
          tw-px-[15px]
        "
      >
        {{ 'main.program.shop' | translate: 'Program Shop' }}
      </div>
    </div>

    <div class="program-panel tw-mt-[8px] tw-p-[16px]">
      <div class="tw-flex tw-flex-wrap tw-gap-[20px] tw-items-start">

        <!-- Left column — filters, product list, terms -->
        <div class="program-side tw-flex tw-flex-col">

          <!-- Filter row -->
          <div class="filter-row tw-flex tw-flex-wrap tw-items-end tw-gap-[16px] tw-pb-[16px]">

            <!-- Date -->
            <div class="filter-field tw-flex tw-flex-col">
              <span class="filter-field__label">
                {{ 'program.shop.select.date' | translate: 'Select Date' }}
              </span>
              <mat-form-field
                appearance="outline"
                class="date-input"
                subscriptSizing="dynamic"
              >
                <input
                  matInput
                  [matDatepicker]="picker"
                  [max]="maxDate"
                  [value]="formVal['date'].value"
                  (dateChange)="setDate($event)"
                  placeholder="{{ 'ui.wager.mm.dd.yyyy' | translate: 'MM/DD/YYYY' }}"
                  data-cy="program-date-input"
                />
                <mat-datepicker-toggle matIconSuffix [for]="picker">
                  <img
                    matDatepickerToggleIcon
                    src="/assets/images/wager/calendar_icon.svg"
                    alt="{{ 'ui.wager.open.calendar' | translate: 'Open calendar' }}"
                    class="date-input__toggle-icon"
                  />
                </mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
              </mat-form-field>
            </div>

            <!-- Track -->
            <div class="filter-field tw-flex tw-flex-col">
              <span class="filter-field__label">
                {{ 'program.shop.select.track' | translate: 'Select Track' }}
              </span>
              <mat-form-field
                appearance="outline"
                class="track-input"
                subscriptSizing="dynamic"
              >
                <mat-select
                  panelClass="program-track-panel"
                  [value]="selectedTrackId"
                  (selectionChange)="selectTrack($event)"
                  placeholder="{{ 'generic.track' | translate: 'Track' }}"
                  disableOptionCentering
                  data-cy="program-track-dropdown"
                >
                  <mat-option [value]="''">
                    {{ 'ui.wager.all.tracks' | translate: 'All Tracks' }}
                  </mat-option>
                  <!-- Favorites lead the list, split off by a dashed rule -->
                  <mat-option
                    *ngFor="let track of sortedTracks; let i = index; trackBy: trackTrackFn"
                    [value]="track.id"
                    [class.track-option--favorite-end]="
                      i === favoriteTrackCount - 1
                      && sortedTracks.length > favoriteTrackCount
                    "
                  >
                    {{ track.name }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <!-- Product list — scrolls past eight rows, header stays put -->
          <div class="program-table-scroll tw-overflow-auto tw-w-full">
            <table class="program-table tw-w-full" data-cy="program-table">
              <thead>
                <tr>
                  <th>{{ 'generic.track' | translate: 'Track' }}</th>
                  <th>{{ 'program.shop.product' | translate: 'Product' }}</th>
                  <th>{{ 'program.shop.price' | translate: 'Price' }}</th>
                  <th class="tw-w-[130px]"></th>
                </tr>
              </thead>
              <tbody>
                @if (isLoadingPrograms) {
                  <!-- Shimmer rows keep the column widths while loading -->
                  @for (row of [1,2,3,4,5,6]; track row) {
                    <tr class="skeleton-row" aria-hidden="true" data-cy="program-row-skeleton">
                      <td>
                        <div class="program-skeleton__bar tw-h-[14px] tw-w-[70%] tw-rounded-[4px]"></div>
                      </td>
                      <td>
                        <div class="program-skeleton__bar tw-h-[14px] tw-w-[85%] tw-rounded-[4px]"></div>
                      </td>
                      <td>
                        <div class="program-skeleton__bar tw-h-[14px] tw-w-[50%] tw-rounded-[4px]"></div>
                      </td>
                      <td>
                        <div class="program-skeleton__bar tw-h-[26px] tw-w-[110px] tw-rounded-[20px] tw-mx-auto"></div>
                      </td>
                    </tr>
                  }
                } @else if (!filteredProducts.length) {
                  <tr class="empty-row">
                    <td colspan="4" class="tw-text-center tw-py-[24px]" data-cy="program-empty">
                      {{
                        'program.shop.no.programs.found'
                          | translate: 'No programs found for this date and/or track.'
                      }}
                    </td>
                  </tr>
                } @else {
                  <tr
                    *ngFor="let product of filteredProducts; trackBy: trackProductFn"
                    [class.active-row]="isActiveProduct(product)"
                    [attr.data-cy]="'program-row-' + product?.ID"
                  >
                    <td>{{ product?.EventName }}</td>
                    <td>{{ product?.ProdName }}</td>
                    <td>
                      @if (product?.Owned) {
                        {{ 'program.shop.purchased' | translate: 'Purchased' }}
                      } @else {
                        ${{ product?.RetailPrice | numberWithCommas: 2 }}
                      }
                    </td>
                    <td class="tw-text-center">
                      @if (product?.Owned) {
                        <button
                          mat-flat-button
                          type="button"
                          class="download-btn"
                          [disabled]="isLoadingPdf"
                          (click)="openProduct(product)"
                          [attr.data-cy]="'program-download-' + product?.ID"
                        >
                          <mat-icon class="btn-icon">download</mat-icon>
                          {{ 'program.shop.download' | translate: 'Download' }}
                        </button>
                      } @else {
                        <button
                          mat-flat-button
                          type="button"
                          class="buy-btn"
                          [disabled]="isLoadingPdf"
                          (click)="openProduct(product)"
                          [attr.data-cy]="'program-download-' + product?.ID"
                        >
                          <mat-icon class="btn-icon">attach_money</mat-icon>
                          {{ 'program.shop.buy.download' | translate: 'Buy & Download' }}
                        </button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <!-- Terms and conditions -->
          <p class="tw-text-right tw-mt-[12px]">
            <a
              href="javascript:void(0)"
              class="terms-link"
              (click)="openTerms()"
              data-cy="program-terms-link"
            >
              {{ 'program.shop.terms.conditions' | translate: 'Data Product Terms and Conditions' }}
            </a>
          </p>
        </div>

        <!-- Right column — PDF viewer -->
        <div class="program-viewer tw-flex-1" data-cy="program-viewer">
          @if (isLoadingPdf) {
            <div class="viewer-placeholder tw-flex tw-flex-col tw-items-center tw-justify-center">
              <mat-progress-spinner mode="indeterminate" diameter="42"></mat-progress-spinner>
            </div>
          } @else if (pdfUrl) {
            <div class="viewer-toolbar tw-flex tw-items-center tw-justify-between tw-gap-[12px]">
              <span class="viewer-toolbar__name" data-cy="program-pdf-name">{{ pdfFileName }}</span>
              <button
                mat-flat-button
                type="button"
                class="download-btn"
                (click)="savePdf()"
                data-cy="program-save-pdf"
              >
                <mat-icon class="btn-icon">download</mat-icon>
                {{ 'program.shop.download' | translate: 'Download' }}
              </button>
            </div>
            <iframe
              class="viewer-frame"
              [src]="pdfUrl"
              title="{{ 'main.program.shop' | translate: 'Program Shop' }}"
              data-cy="program-pdf-frame"
            ></iframe>
          } @else {
            <div class="viewer-placeholder tw-flex tw-flex-col tw-items-center tw-justify-center">
              <mat-icon class="viewer-placeholder__icon">picture_as_pdf</mat-icon>
              <span data-cy="program-viewer-message">
                {{
                  downloadError
                    || ('program.shop.select.a.program' | translate: 'Select a program to view it.')
                }}
              </span>
            </div>
          }
        </div>
      </div>
    </div>
  </section>
}

<!-- Favorites row — shared so it shows in the shop and above the profile panel -->
<ng-template #favoritesBar>
  @if (userEmail !== undefined && userEmail !== 'Guest') {
    <div class="favorites-section tw-mb-[12px]" data-cy="program-favorites">
      <img
        src="/assets/images/wager/favorites_icon.svg"
        class="tw-inline tw-mr-[19px] tw-float-left"
        alt="favorites"
      >
      <span
        class="
          tw-text-[18px]
          tw-relative
          tw-top-[3px]
          tw-mt-[5px]
          tw-float-left
        "
      >
        {{ 'ui.wager.favorites' | translate: 'Favorites' }}
      </span>
      <mat-icon
        (click)="scrollTo('prev')"
        class="
          tw-text-[25px]
          tw-text-[#B5C5D8]
          tw-relative
          tw-cursor-pointer
          tw-top-[3px]
          tw-mt-[5px]
          tw-ml-[20px]
          tw-mr-[20px]
          tw-float-left
        "
        data-cy="program-favorites-prev"
      >
        arrow_back
      </mat-icon>
      <div
        #favorites
        class="favorite-lists tw-w-[80%] tw-flex tw-mt-[5px] tw-float-left tw-overflow-hidden"
      >
        <!-- Skeleton chips while the favorites preference resolves -->
        @if (showFavoritesSkeleton) {
          <ul
            class="tw-text-center tw-flex"
            aria-hidden="true"
            data-cy="program-favorites-skeleton"
          >
            @for (chip of [1,2,3,4]; track chip) {
              <li
                class="
                  program-skeleton__bar
                  tw-w-[273px]
                  tw-h-[30px]
                  tw-inline-block
                  tw-rounded-[4px]
                  tw-mr-[18px]
                "
              ></li>
            }
          </ul>
        }
        @else {
          <ul class="tw-text-center tw-flex">
            <li
              *ngFor="let favorite of listOfFavoriteEvents; trackBy: trackFavoriteFn"
              (click)="selectFavorite(favorite)"
              class="
                tw-text-[#FFFFFF]
                tw-w-[273px]
                tw-h-[30px]
                tw-content-center
                tw-inline-block
                tw-rounded-[4px]
                tw-bg-[#557DAC]
                tw-mr-[18px]
              "
              [ngClass]="{'active-event' : selectedTrackId === favorite.id}"
              [attr.data-cy]="'program-favorite-' + favorite.id"
            >
              <!-- Mirror the wager chips: track (country code), race, status/MTP -->
              <span data-cy="favorite-event-name">
                {{
                  favorite?.CountryCode
                    ? favorite?.CountryCode === 'US' || favorite?.CountryCode === 'CA'
                      ? favorite?.ToteResponse?.EventDetail?.EventName
                      : favorite?.CountryCode + '-' + favorite?.ToteResponse?.EventDetail?.EventName
                    : favorite?.ToteResponse?.EventDetail?.EventName
                }}
              </span>

              <span data-cy="favorite-current-race">
                {{
                  favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId
                    ? '('+(favorite?.ToteResponse?.EventDetail?.TrackType | getTrackType).charAt(0)
                      + ' R'+favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId+')'
                    : ''
                }}
              </span>

              <span data-cy="favorite-event-status">
                {{
                  favorite?.ToteResponse?.EventDetail?.currentRace
                    ? (
                        (favorite?.ToteResponse?.EventDetail?.currentRace)?.RaceStatus
                          | getRaceStatus: (
                            favorite?.ToteResponse?.EventDetail?.currentRace
                          )?.PostTime: favorite?.eventName
                          | countdown: false: favorite?.eventName | async
                      )
                    : (favorite?.ToteResponse?.EventDetail?.EventStatus | getEventStatus)
                }}
              </span>
            </li>
          </ul>
        }
      </div>
      <mat-icon
        (click)="scrollTo('next')"
        class="
          tw-text-[25px]
          tw-text-[#B5C5D8]
          tw-relative
          tw-cursor-pointer
          tw-top-[3px]
          tw-mt-[5px]
          tw-ml-[20px]
          tw-mr-[20px]
          tw-float-left
        "
        data-cy="program-favorites-next"
      >
        arrow_forward
      </mat-icon>
    </div>
  }
</ng-template>
